/**
 * @fileoverview Countdown timer: elements with [vdx-countdown-enddate] show days/hours/mins/secs until end (and optionally from [vdx-countdown-startdate]).
 */

import { logWarn, showErrorOnElement } from './errors.js';

const MODULE = 'countdowntimer';

const SELECTOR_ENDDATE = '[vdx-countdown-enddate]';
const ATTR_ENDDATE = 'vdx-countdown-enddate';
const ATTR_STARTDATE = 'vdx-countdown-startdate';
const SELECTOR_DAYS = '[vdx-countdown-days]';
const SELECTOR_HOURS = '[vdx-countdown-hours]';
const SELECTOR_MINS = '[vdx-countdown-mins]';
const SELECTOR_SECS = '[vdx-countdown-secs]';

const INTERVAL_MS = 1000;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/** Attribute set when a countdown has been initialized to avoid duplicate intervals. */
const WIRED_ATTR = 'vdx-wired';

/**
 * Writes a value to an element's textContent if the element exists.
 * @param {Element | null} el
 * @param {string | number} value
 */
function setText(el, value) {
    if (el && typeof el.textContent !== 'undefined') el.textContent = String(value);
}

/**
 * Runs one countdown: updates days/hours/mins/secs every second until end. Stops when distance <= 0.
 * @param {Element} container - Element with [vdx-countdown-enddate]; children with -days/-hours/-mins/-secs are updated.
 * @param {number} startTime - Start timestamp (ms).
 * @param {number} endTime - End timestamp (ms).
 */
export function countdown(container, startTime, endTime) {
    if (!container) return;
    const daysEl = container.querySelector(SELECTOR_DAYS);
    const hoursEl = container.querySelector(SELECTOR_HOURS);
    const minsEl = container.querySelector(SELECTOR_MINS);
    const secsEl = container.querySelector(SELECTOR_SECS);

    if (Number.isNaN(endTime) || endTime <= 0) return;
    const start = Number.isNaN(startTime) ? Date.now() : startTime;

    let intervalId = null;
    function update() {
        const now = Date.now();
        const from = now > start ? now : start;
        const distance = endTime - from;

        if (distance <= 0) {
            setText(daysEl, '0');
            setText(hoursEl, '0');
            setText(minsEl, '0');
            setText(secsEl, '0');
            if (intervalId != null) clearInterval(intervalId);
            return;
        }

        const days = Math.floor(distance / MS_PER_DAY);
        const hours = Math.floor((distance % MS_PER_DAY) / MS_PER_HOUR);
        const minutes = Math.floor((distance % MS_PER_HOUR) / MS_PER_MINUTE);
        const seconds = Math.floor((distance % MS_PER_MINUTE) / MS_PER_SECOND);

        setText(daysEl, days);
        setText(hoursEl, hours);
        setText(minsEl, minutes);
        setText(secsEl, seconds);
    }

    update();
    intervalId = setInterval(update, INTERVAL_MS);
}

/**
 * Parses an attribute value as a date and returns timestamp, or NaN if invalid.
 * @param {string | null} value
 * @returns {number}
 */
function parseDate(value) {
    if (value == null || String(value).trim() === '') return NaN;
    const t = new Date(value.trim()).getTime();
    return t;
}

/**
 * Initializes all [vdx-countdown-enddate] elements: parses end (and optional start) date and starts countdown.
 * Each container is wired at most once. Invalid dates are logged and shown on the element (Webflow).
 */
export function initializeCountdowns() {
    if (typeof document === 'undefined') return;
    const elements = document.querySelectorAll(SELECTOR_ENDDATE);
    elements.forEach((element) => {
        if (element.hasAttribute(WIRED_ATTR)) return;
        element.setAttribute(WIRED_ATTR, '');

        const endDateAttr = element.getAttribute(ATTR_ENDDATE);
        const endTime = parseDate(endDateAttr);

        if (Number.isNaN(endTime)) {
            const msg = `vdx-countdown-enddate must be a valid date (e.g. ISO "2025-12-31" or "Dec 31, 2025"). Got: "${endDateAttr ?? ''}".`;
            logWarn(msg, { module: MODULE, context: { value: endDateAttr } });
            showErrorOnElement(element, msg);
            return;
        }

        const startDateAttr = element.getAttribute(ATTR_STARTDATE);
        const startTime = parseDate(startDateAttr);
        const start = Number.isNaN(startTime) ? Date.now() : startTime;

        if (start >= endTime) {
            const msg = 'vdx-countdown-startdate must be before vdx-countdown-enddate.';
            logWarn(msg, { module: MODULE });
            showErrorOnElement(element, msg);
            return;
        }

        countdown(element, start, endTime);
    });
}
